import { getAllAssignUsers, getAssignedUsers } from '@api/users/users.service';
import { CostCenterUserAssignTableDataDto } from '@interfaces/models';
import { useAssignTable } from '@shared/components/assign-table';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUsers } from '@pages/cost-centers/hooks';
import { useState, useMemo, useRef } from 'react';
import { PaginationState } from '@ui/components';
import { useHasPermission } from '@shared/hooks';
import { getTableRequest } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { useDebouncedState } from '@ui/hooks';

import { getUsersColumns } from './cost-centers-assign-users-table-columns';

interface UsersTableState {
  pagination: PaginationState;
  globalFilter: string;
}

const usersTableInitialState: UsersTableState = {
  pagination: {
    pageIndex: 0,
    pageSize: 4,
  },
  globalFilter: '',
};

export const useCostCenterAssignUsers = (
  assignedUsers: string[],
  assignAllUsersSuccess: (data: string[]) => void,
  editMode?: boolean,
) => {
  const { t } = useTranslation();
  const hasEditPermission = useHasPermission('COST_CENTER_ASSIGN_USERS', 'WRITE');
  const columns = useMemo(() => getUsersColumns(t), [t]);
  const [userSearch, setUserSearch] = useDebouncedState<string>('', 800);
  const [state, setState] = useState<UsersTableState>(usersTableInitialState);
  const { usersOptions, isLoadingUsersOptions } = useUsers(userSearch);
  const totalItemsRef = useRef(0);

  const request = useMemo(() => {
    return getTableRequest({
      pagination: state.pagination,
      search: state.globalFilter,
    });
  }, [state]);

  const {
    data: selectedUsers,
    isLoading: isSelectedUsersLoading,
    isFetching: isSelectedUsersFetching,
  } = useQuery({
    queryKey: ['cost-centers-users-table', request, assignedUsers],
    queryFn: () => getAssignedUsers({ ...request, ids: assignedUsers }),
  });

  const { mutate: assignAllUsers, isPending: isGetAllAssignUsersPending } = useMutation({
    mutationFn: () => getAllAssignUsers(),
    onSuccess: (data) => {
      assignAllUsersSuccess(data.map((user) => user.id));
    },
  });

  const totalItems = useMemo(() => {
    if (!isSelectedUsersFetching) {
      totalItemsRef.current = selectedUsers?.pagination.totalItems ?? 0;
    }
    return totalItemsRef.current;
  }, [selectedUsers, isSelectedUsersFetching]);

  const assignTable = useAssignTable<CostCenterUserAssignTableDataDto>({
    isLoading: isSelectedUsersLoading && isSelectedUsersFetching,
    hideColumns: { select: !hasEditPermission },
    data: selectedUsers?.data ?? [],
    phraseChange: setUserSearch,
    getRowId: (row) => row.id,
    options: usersOptions,
    onStateChange: (state) => {
      setState(state as UsersTableState);
    },
    enableRowSelection: (row) => !editMode || !row.original.cannotBeDetached,
    isOptionLoading: isLoadingUsersOptions,
    initialState: state,
    totalItems,
    columns,
  });

  return { assignTable, assignAllUsers, isGetAllAssignUsersPending };
};
